/**
 * Which chart settings popup is open (screenshots/72, 76–79) and the overview cross-filter (79, 81 #9, 89).
 *
 * - `open`: the popup for one Chart / Table / Number widget. "add" = the widget was just added from the library (Cancel
 *   takes it off again); "edit" = ⋯ → Edit chart.
 * - `overviewCrossFilter` (interactions.ts createCrossFilterStore): a mark clicked in a chart whose Click is "Filter the
 *   overview". Every widget that has that field follows: data widgets re-query with the filter (withCrossFilter), member
 *   widgets keep only that member (crossFilterFeeds). The bar above the grid says what's filtered; Esc or Clear clears it.
 */
import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import { BUILTIN_THEMES, type ChartTheme } from "./charts/themes";
import { createThemeStore, type ThemePut, type ThemeStore } from "./charts/themeStore";
import { createCrossFilterStore, withCrossFilter, type CrossFilter } from "./charts/interactions";
import type { LayoutItem, WidgetFeeds, WidgetType } from "./types";

export type { CrossFilter };

export interface ChartEditorState {
  open: { widgetId: string; mode: "add" | "edit" } | null;
  announce: string;
  edit: (widgetId: string, mode?: "add" | "edit") => void;
  close: () => void;
}

export const chartEditorStore = createStore<ChartEditorState>()((set) => ({
  open: null, announce: "",
  edit: (widgetId, mode = "edit") => set({ open: { widgetId, mode } }),
  close: () => set({ open: null }),
}));
export const useChartEditor = <T,>(sel: (s: ChartEditorState) => T) => useStore(chartEditorStore, sel);

/** One cross-filter per page (the Overview). */
export const overviewCrossFilter = createCrossFilterStore();
export const useOverviewFilter = () => useStore(overviewCrossFilter, (s) => s.filter);
export const setOverviewFilter = (f: CrossFilter | null) => {
  overviewCrossFilter.getState().set(f);
  chartEditorStore.setState({ announce: f ? `Filtering the overview: ${f.label}` : "Filter cleared" });
};

/** Widgets that follow a member cross-filter (79 "Also filters"): their member lists keep only that member. */
export const MEMBER_WIDGETS: WidgetType[] = ["tossUps", "targetList", "whipCount", "allies"];
export const MEMBER_FIELDS = new Set(["recipient", "member", "name", "sponsor"]);

/** The other widgets on the page that a filter on `field` reaches. */
export function crossTargets(items: LayoutItem[], field: string | undefined, self: string): LayoutItem[] {
  if (!field) return [];
  return items.filter((i) => i.id !== self && (
    (MEMBER_FIELDS.has(field) && MEMBER_WIDGETS.includes(i.type))
    || (!!i.config.settings && withCrossFilter(i.config.settings.data, { field, value: "", label: "", sourceWidgetId: self }, i.id).applied)));
}

/** A member's ward from the row keys member datasets use ("ward:39"). */
const wardOf = (v: string) => { const m = /^(?:ward:)?(\d{1,2})$/.exec(v); return m ? Number(m[1]) : null; };
const sameMember = (f: CrossFilter) => (m: { name: string; ward?: number; personId?: string }) =>
  m.personId === f.value || m.name === f.label || (wardOf(f.value) !== null && m.ward === wardOf(f.value));

/** Member widgets' feeds under a member cross-filter (pure; tests/interactions.test.ts). */
export function crossFilterFeeds(feeds: WidgetFeeds, f: CrossFilter | null): WidgetFeeds {
  if (!f || !MEMBER_FIELDS.has(f.field)) return feeds;
  const keep = sameMember(f);
  return {
    ...feeds,
    ...(feeds.tossUps ? { tossUps: { ...feeds.tossUps, members: feeds.tossUps.members.filter(keep) } } : {}),
    ...(feeds.targetList ? { targetList: { members: feeds.targetList.members.filter(keep) } } : {}),
    ...(feeds.whipCount ? { whipCount: { ...feeds.whipCount, members: feeds.whipCount.members.filter(keep) } } : {}),
    ...(feeds.allies ? { allies: { members: feeds.allies.members.filter(keep) } } : {}),
  };
}

/** Last name for the chip ("Nugent"). */
export const shortName = (label: string) => label.replace(/\s+\((\d+)\)$/, "").trim().split(/\s+/).filter((w) => !/^(jr\.?|sr\.?|ii|iii)$/i.test(w)).at(-1) ?? label;

// ---------------------------------------------------------------- chart themes (/api/themes, §6 ticket 19)

/** Saves carry the updatedAt they started from; 409 → `{ conflict }` with their theme (90), which themeStore merges. */
const put: ThemePut = async (t, baseUpdatedAt) => {
  const r = await fetch(`/api/themes/${encodeURIComponent(t.id)}`, { method: "PUT", credentials: "include", headers: { "content-type": "application/json" },
    body: JSON.stringify({ theme: t, baseUpdatedAt }) });
  if (r.status === 409) return { ok: false, conflict: ((await r.json()) as { conflict: ChartTheme }).conflict };
  if (!r.ok) throw new Error(String(r.status));
  return { ok: true, value: ((await r.json()) as { theme: ChartTheme }).theme };
};
export const themeStore: ThemeStore = createThemeStore({
  put,
  del: async (id) => { await fetch(`/api/themes/${encodeURIComponent(id)}`, { method: "DELETE", credentials: "include" }); },
  setDefault: async (id) => { await fetch("/api/themes", { method: "PUT", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ teamDefault: id }) }); },
});

let loaded = false;
/** Saved themes: from the overview snapshot on first paint, refreshed from /api/themes once. */
export function loadThemes(saved?: ChartTheme[], teamDefault?: string, force = false) {
  if (saved) themeStore.getState().load(saved, teamDefault);
  if (loaded && !force) return;
  loaded = true;
  fetch("/api/themes", { credentials: "include" }).then((r) => (r.ok ? r.json() : null))
    .then((d: { saved: ChartTheme[]; teamDefault: string } | null) => { if (d?.saved) themeStore.getState().load(d.saved, d.teamDefault); }).catch(() => {});
}
export const themeById = (themes: ChartTheme[], id?: string) => themes.find((t) => t.id === id) ?? BUILTIN_THEMES[0];
